import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight, FileText, Phone, Plus, ShieldCheck } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { cn, money } from '@/lib/utils';
import {
  Alert,
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Skeleton,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { APPLICATION_STATUS, GUARANTOR_STATUS } from '@/lib/status';

interface Application {
  id: string;
  applicationNumber: string;
  productName: string;
  requestedAmount: string;
  approvedAmount: string | null;
  installmentCount: number;
  purpose: string;
  status: string;
  submittedAt: string | null;
  decisionReason: string | null;
  loanId: string | null;
  loanNumber: string | null;
  guarantorsRequired: number;
  guarantorsConfirmed: number;
  guarantorsOutstanding: number;
  guarantors: Array<{
    id: string;
    fullName: string;
    relationship: string;
    phone: string;
    status: string;
    confirmedAt: string | null;
    declineReason: string | null;
  }>;
}

const DECIDED = ['APPROVED', 'REJECTED', 'WITHDRAWN'];

/**
 * A member's applications.
 *
 * Distinct from My loans, and the page says so: an application is the *request*
 * and ends at a decision; a loan is the money owed and only exists after
 * approval. Once decided, an application collapses to a summary that points at
 * its loan — showing a live guarantor tracker on an approved application makes
 * it look like something is still outstanding when nothing is.
 */
export default function MyApplications() {
  const { data = [], isLoading } = useQuery({
    queryKey: ['my-applications'],
    queryFn: () => api.get<Application[]>('/loan-applications/my'),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  const inProgress = data.filter((a) => !DECIDED.includes(a.status));
  const decided = data.filter((a) => DECIDED.includes(a.status));

  const stalled = inProgress.filter((a) =>
    a.guarantors.some((g) => g.status === 'DECLINED' || g.status === 'UNREACHABLE'),
  );

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow="Your requests"
        title="Applications"
        description="Approved applications become loans under My loans."
        actions={
          <Link to="/portal/loans/apply">
            <Button>
              <Plus size={16} /> New application
            </Button>
          </Link>
        }
      />

      {stalled.length > 0 && (
        <Alert tone="warning" title="A guarantor could not be confirmed" className="mb-6">
          Someone you named either declined or could not be reached. Call UNIFEMGA on{' '}
          <a href="tel:+2349168484980" className="font-bold underline">
            (+234) 916 848 4980
          </a>{' '}
          to give another name, or ask your guarantor to expect the call.
        </Alert>
      )}

      {data.length === 0 ? (
        <EmptyState
          icon={<FileText size={22} />}
          title="No applications yet"
          description="Apply for an interest-free loan and you will be able to follow its progress here."
          action={
            <Link to="/portal/loans/apply">
              <Button>Apply for a loan</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-8">
          {inProgress.length > 0 && (
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground-secondary">
                In progress
              </h2>
              <div className="space-y-4">
                {inProgress.map((application) => (
                  <ActiveCard key={application.id} application={application} />
                ))}
              </div>
            </section>
          )}

          {decided.length > 0 && (
            <section>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-foreground-secondary">
                Decided
              </h2>
              <div className="space-y-2">
                {decided.map((application) => (
                  <DecidedCard key={application.id} application={application} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

/** An application still working its way to a decision. */
function ActiveCard({ application }: { application: Application }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="numeric font-heading font-bold text-foreground">
              {application.applicationNumber}
            </p>
            <p className="text-sm text-foreground-secondary">
              {application.productName} · {application.installmentCount} repayments
            </p>
            {application.submittedAt && (
              <p className="mt-1 text-xs text-foreground-secondary">
                Submitted {formatLongDate(new Date(application.submittedAt))}
              </p>
            )}
          </div>

          <div className="text-right">
            <p className="display-figure text-xl text-foreground">
              {money(application.requestedAmount)}
            </p>
            <StatusBadge map={APPLICATION_STATUS} status={application.status} className="mt-1.5" />
          </div>
        </div>

        <p className="mt-4 rounded-xl bg-muted px-4 py-3 text-sm text-foreground">
          {application.purpose}
        </p>

        {application.decisionReason && (
          <Alert tone="warning" className="mt-4">
            {application.decisionReason}
          </Alert>
        )}

        {application.guarantorsRequired > 0 && (
          <div className="mt-5 border-t border-border pt-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <ShieldCheck size={15} className="text-foreground-secondary" />
              <span className="text-sm font-semibold text-foreground">Guarantors</span>
              <span
                className={cn(
                  'numeric rounded-full px-2 py-0.5 text-xs font-bold',
                  application.guarantorsConfirmed >= application.guarantorsRequired
                    ? 'bg-success-soft text-success-soft-foreground'
                    : 'bg-warning-soft text-warning-soft-foreground',
                )}
              >
                {application.guarantorsConfirmed}/{application.guarantorsRequired} confirmed
              </span>
            </div>

            <div className="space-y-2">
              {application.guarantors.map((guarantor) => (
                <div
                  key={guarantor.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3.5 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground">
                      {guarantor.fullName}
                    </p>
                    <p className="numeric text-xs text-foreground-secondary">
                      {guarantor.relationship} · {guarantor.phone}
                    </p>
                    {guarantor.declineReason && (
                      <p className="mt-1 text-xs text-danger">
                        &ldquo;{guarantor.declineReason}&rdquo;
                      </p>
                    )}
                  </div>
                  <StatusBadge map={GUARANTOR_STATUS} status={guarantor.status} />
                </div>
              ))}
            </div>

            {application.guarantorsOutstanding > 0 && (
              <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-foreground-secondary">
                <Phone size={12} className="mt-0.5 shrink-0" />
                UNIFEMGA will telephone{' '}
                {application.guarantorsOutstanding === 1
                  ? 'the remaining guarantor'
                  : `the remaining ${application.guarantorsOutstanding} guarantors`}{' '}
                to confirm. Please let them know to expect the call.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A decided application.
 *
 * Compact on purpose. The decision is made, so the live detail now belongs to
 * the loan; repeating it here just makes two pages look like duplicates.
 */
function DecidedCard({ application }: { application: Application }) {
  const to =
    application.status === 'APPROVED' && application.loanId
      ? `/portal/loans/${application.loanId}`
      : null;

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <p className="numeric text-sm font-semibold text-foreground">
          {money(application.approvedAmount ?? application.requestedAmount)}
        </p>
        <p className="truncate text-xs text-foreground-secondary">
          {application.applicationNumber}
          {application.loanNumber ? ` · ${application.loanNumber}` : ''}
        </p>
        {application.decisionReason && (
          <p className="mt-1 text-xs leading-relaxed text-danger">{application.decisionReason}</p>
        )}
      </div>

      <StatusBadge map={APPLICATION_STATUS} status={application.status} />
      {to && <ChevronRight size={15} className="shrink-0 text-foreground-secondary" />}
    </>
  );

  const className =
    'flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3 text-left';

  return to ? (
    <Link to={to} className={cn(className, 'transition-colors hover:bg-accent')}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}
